import { MoneySchema } from "@redotech/money";
import { Currency } from "@redotech/money/currencies";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod/v4";
import { MercuryModelMode } from "../conversion/mercury-model";
import { CorrelatedCarrierServiceSchema } from "../fulfillments/fulfillment-carriers-and-services";
import {
  DynamicRateRounding,
  RateTableType,
} from "../integration/redo-in-shipping/shipping-rate-table";

const baseRedoShippingOptionSchema = z.object({
  provider: z.literal("redo"),
  rateTableId: zExt.objectId(),
  checkoutTreeNodeId: z.string(),
  assignedTreatmentId: z.string().optional(),
});

const markupSchema = z.object({
  markupPercentage: z.string().optional(),
  markupAmount: z.string().optional(),
});

const costBreakdownSchema = z.object({
  costCurrency: z.enum(Currency),
  labelCost: z.string(),
  markup: markupSchema.optional(),
  rounding: z.enum(DynamicRateRounding).optional(),
});
export type CostBreakdown = z.infer<typeof costBreakdownSchema>;

const dynamicRedoShippingOptionSchema = baseRedoShippingOptionSchema.extend({
  rateType: z.literal(RateTableType.DYNAMIC),
  carrierService: CorrelatedCarrierServiceSchema,
  costBreakdown: costBreakdownSchema,
  promisedDeliveryDate: z.string().optional(),
});

const fixedRedoShippingOptionSchema = baseRedoShippingOptionSchema.extend({
  rateType: z.literal(RateTableType.FIXED),
});

export const checkoutShippingOptionSchema = z.union([
  dynamicRedoShippingOptionSchema,
  fixedRedoShippingOptionSchema,
]);

export type CheckoutShippingOption = z.infer<
  typeof checkoutShippingOptionSchema
>;

export const featureValuePairSchema = z.array(
  z.tuple([z.string(), z.number()]),
);
export const optionWeightsSchema = z.record(z.string(), featureValuePairSchema);

const mercuryResultSchema = z.object({
  checkoutExperienceId: z.string(),
  checkoutTreeNodeId: z.string(),
  modelMode: z.enum(MercuryModelMode),
  optimalPrices: z.record(z.string(), z.number()),
  expectedMargin: z.number(),
  expectedMarginsByRateTable: z.record(z.string(), z.number()),
  labelCostShopMoneyByRateTableId: z.record(z.string(), MoneySchema).nullish(),
  priceCombinationsExplored: z.number().nullish(),
  betaWeightsMean: optionWeightsSchema.nullish(),
  betaWeightsStd: optionWeightsSchema.nullish(),
  betaWeightsSampled: optionWeightsSchema.nullish(),
  featuresWithOptimalPrices: featureValuePairSchema.nullish(),
  inferenceTimeMs: z.number(),
});
export interface MercuryResult extends z.infer<typeof mercuryResultSchema> {}

export const checkoutShippingOptionsDocumentSchema = z.object({
  _id: zExt.objectId(),
  stableId: z.string().optional(),
  team: zExt.objectId(),
  options: z.array(checkoutShippingOptionSchema),
  checkoutExperienceIdToMercuryModelId: z
    .record(z.string(), z.string().nullish())
    .nullish(),
  mercuryResults: z.array(mercuryResultSchema).nullish(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type CheckoutShippingOptionsDocument = z.infer<
  typeof checkoutShippingOptionsDocumentSchema
>;

/**
 * Input schema for creating a new document (without timestamps and _id)
 */
export const createCheckoutShippingOptionsDocumentSchema =
  checkoutShippingOptionsDocumentSchema.omit({
    _id: true,
    stableId: true,
    createdAt: true,
    updatedAt: true,
  });

export type CreateCheckoutShippingOptionsDocument = z.infer<
  typeof createCheckoutShippingOptionsDocumentSchema
>;
